// apps/web/src/lib/destino-inicial.ts — Página inicial escolhida pelas permissões da sessão.
//
// Cada perfil cai na tela que é o seu trabalho principal: o operador no Kanban do tablet e os
// demais no Painel (o almoxarifado também: o bloco dele tem link direto para a fila de abastecimento).
// O Painel abre para quem enxerga ao menos um bloco dele (L-73): o Compras cai no Painel com o bloco compras,
// sem permissão nova — a visão geral continua só para `painel:consultar`.
// Quem não enxerga nenhum bloco vai para o primeiro módulo visível no menu — nunca para uma página proibida.
// Ergonomia apenas: a autorização real continua nos guards do backend.
import { ARVORE_NAVEGACAO, filtrarNavegacaoPorPermissoes, type GrupoNavegacao } from '../components/shell/navegacao';
import { GATE_BLOCO } from './painel/blocos';

export const ROTA_PAINEL = '/painel';
export const ROTA_KANBAN_TABLET = '/coletor/kanban';

/**
 * Permissões que abrem algum bloco do Painel (espelha `painel-blocos.controller.ts` e `GATE_BLOCO`, que é a
 * fonte única): `planejamento:gerenciar`, `qualidade:gerenciar`, `estoque:separar`, `estoque:entregar`,
 * `compras:gerenciar`, `pv:fechar` e `painel:consultar` (visão geral).
 */
export const PERMISSOES_DE_BLOCO_DO_PAINEL: readonly string[] = [...new Set(Object.values(GATE_BLOCO).flat())];

/** O Painel abre para quem tem `painel:consultar` OU a permissão de algum bloco (L-73). */
export function podeAbrirPainel(permissoes: readonly string[]): boolean {
  return PERMISSOES_DE_BLOCO_DO_PAINEL.some((p) => permissoes.includes(p));
}

/** Primeiro destino do menu que exige alguma permissão (o Painel não exige e é tratado à parte). */
export function primeiroModuloPermitido(
  permissoes: readonly string[],
  arvore: readonly GrupoNavegacao[] = ARVORE_NAVEGACAO,
): string | undefined {
  for (const grupo of filtrarNavegacaoPorPermissoes(permissoes, arvore)) {
    const item = grupo.itens?.find((i) => i.permissaoExigida);
    if (item) return item.caminho;
    if (grupo.caminho && grupo.permissaoExigida) return grupo.caminho;
  }
  return undefined;
}

/** Rota inicial depois do login, decidida só pelas permissões da sessão. */
export function destinoInicial(
  permissoes: readonly string[],
  arvore: readonly GrupoNavegacao[] = ARVORE_NAVEGACAO,
): string {
  const tem = (p: string): boolean => permissoes.includes(p);

  // Operador / tablet: aponta produção, não consulta o Painel.
  if (!tem('painel:consultar') && tem('apontamento:create')) return ROTA_KANBAN_TABLET;

  // Quem enxerga ao menos um bloco do Painel começa nele; o Compras cai no bloco compras (L-73, sem permissão nova).
  if (podeAbrirPainel(permissoes)) return ROTA_PAINEL;
  // Sem bloco: o primeiro módulo que a pessoa consegue abrir. Sem nenhum, o Painel mostra o aviso de acesso.
  return primeiroModuloPermitido(permissoes, arvore) ?? ROTA_PAINEL;
}

/**
 * Guarda da rota de entrada (`/` ou `/painel`): devolve para onde redirecionar, ou `null` quando a página
 * atual deve ser exibida. Nunca devolve o próprio `pathname`, então não há laço de redirecionamento.
 */
export function redirecionamentoDeEntrada(pathname: string, permissoes: readonly string[]): string | null {
  let destino: string | null = null;
  if (pathname === '/') destino = destinoInicial(permissoes);
  else if (pathname === ROTA_PAINEL && !podeAbrirPainel(permissoes)) destino = destinoInicial(permissoes);
  return destino && destino !== pathname ? destino : null;
}
